import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import {
  CORE_STEPS,
  FEATURES,
  WALKTHROUGHS,
  type FeatureEntry,
  type WalkthroughEntry,
  type GuideData,
  type CoreStepEntry,
  type EntryPath,
} from "@/lib/guideRegistry";
import { guideSignals } from "@/lib/guideSignals";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type StepStatus = "locked" | "active" | "complete";
export type FeatureStatus = "hidden" | "locked" | "unlocked" | "seen" | "used";

export interface CoreStepState {
  step: CoreStepEntry;
  status: StepStatus;
}

export interface FeatureState {
  feature: FeatureEntry;
  status: FeatureStatus;
  progressText?: string;
}

/**
 * Server-derived counts passed in by the consuming component (originate from
 * React Query caches). Signals are read internally via `guideSignals`.
 */
export interface ServerCounts {
  seedCount: number;
  trawledCount: number;
  scoredCount: number;
  readOrSkimmedCount: number;
  claimCount: number;
  totalPapers: number;
  hasDebateMap: boolean;
  hasClaimsWithEvidence: boolean;
  monitorEnabled: boolean;
  conceptCreatedAt: string;
  digestFrequency: string;
  hasWorkingSets: boolean;
}

// ---------------------------------------------------------------------------
// localStorage helpers
// ---------------------------------------------------------------------------

const USER_KEY = "paperchase-guide-user";
const conceptKey = (conceptId: string) => `paperchase-guide-${conceptId}`;
const legacyDismissedKey = (conceptId: string) =>
  `paperchase-guide-dismissed-${conceptId}`;
const justUnlockedKey = (conceptId: string) =>
  `paperchase-guide-just-unlocked-${conceptId}`;

interface UserGuideStorage {
  graduated: boolean;
  expert: boolean;
  /**
   * Dev-only override that pins the phase regardless of counts. Set by the
   * GuidesCatalogDrawer's [Dev] Phase controls when import.meta.env.DEV is
   * true. Never written in production. When set, blocks the auto-graduation
   * effect so Force Onboarding sticks even on a count-complete concept.
   */
  _devForcedPhase?: "onboarding" | "graduated" | "expert" | null;
  /**
   * Dev-only override that zeroes out all counts before computing
   * coreSteps/features. Lets Force Onboarding show the "Step 1 of 6: Seed"
   * active state and locked-feature pills even on a count-complete concept.
   */
  _devForcedFreshCounts?: boolean;
}

interface ConceptGuideStorage {
  entryPath: EntryPath | null;
  dismissedMilestones: string[];
  seenMilestones: string[];
}

function readUserStorage(): UserGuideStorage {
  const defaults: UserGuideStorage = { graduated: false, expert: false };
  try {
    return { ...defaults, ...JSON.parse(localStorage.getItem(USER_KEY) ?? "{}") };
  } catch {
    return defaults;
  }
}

function writeUserStorage(value: UserGuideStorage): void {
  localStorage.setItem(USER_KEY, JSON.stringify(value));
}

function readConceptStorage(conceptId: string): ConceptGuideStorage {
  const defaults: ConceptGuideStorage = {
    entryPath: null,
    dismissedMilestones: [],
    seenMilestones: [],
  };
  try {
    return {
      ...defaults,
      ...JSON.parse(localStorage.getItem(conceptKey(conceptId)) ?? "{}"),
    };
  } catch {
    return defaults;
  }
}

function writeConceptStorage(conceptId: string, value: ConceptGuideStorage): void {
  localStorage.setItem(conceptKey(conceptId), JSON.stringify(value));
}

function readJustUnlocked(conceptId: string): string[] {
  try {
    const raw = localStorage.getItem(justUnlockedKey(conceptId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeJustUnlocked(conceptId: string, ids: string[]): void {
  try {
    localStorage.setItem(justUnlockedKey(conceptId), JSON.stringify(ids));
  } catch {
    // swallow
  }
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export type GuidePhase = "onboarding" | "graduated" | "expert";

export interface UseGuideStateResult {
  phase: GuidePhase;
  entryPath: EntryPath | null;
  coreSteps: CoreStepState[];
  features: FeatureState[];
  walkthroughs: WalkthroughEntry[];
  justUnlocked: string[];
  /**
   * Composed GuideData (server counts + localStorage signals). Exposed so
   * consumers can pass it to T2 panels for live `isDone` predicate evaluation.
   */
  data: GuideData;
  /** Previous→current phase transition, cleared to null after 3 s. */
  phaseTransition: { from: GuidePhase; to: GuidePhase } | null;
  setEntryPath: (path: EntryPath) => void;
  dismissFeature: (id: string) => void;
  dismissAll: () => void;
  consumeJustUnlocked: (id: string) => void;
}

export function useGuideState(
  conceptId: string,
  counts: ServerCounts,
): UseGuideStateResult {
  // ---- State ----------------------------------------------------------------

  const [userStorage, setUserStorage] = useState<UserGuideStorage>(() =>
    readUserStorage()
  );

  const [conceptStorage, setConceptStorage] = useState<ConceptGuideStorage>(() =>
    readConceptStorage(conceptId)
  );

  // ---- Compose GuideData from counts + signals ----------------------------
  // Read signals on every render so that a component re-render (triggered e.g.
  // by a counts prop change, a setUserStorage call, or the phase-change event)
  // picks up the latest localStorage-backed signals without us maintaining a
  // signals-in-state duplicate.

  const signals = guideSignals.read(conceptId);
  // Dev-only override: substitute zero counts so the bar visualizes a
  // fresh-concept onboarding state even when the real concept is complete.
  const effectiveCounts: ServerCounts = userStorage._devForcedFreshCounts
    ? {
        seedCount: 0,
        trawledCount: 0,
        scoredCount: 0,
        readOrSkimmedCount: 0,
        claimCount: 0,
        totalPapers: 0,
        hasDebateMap: false,
        hasClaimsWithEvidence: false,
        monitorEnabled: false,
        conceptCreatedAt: counts.conceptCreatedAt,
        digestFrequency: counts.digestFrequency,
        hasWorkingSets: false,
      }
    : counts;
  const data: GuideData = { ...effectiveCounts, ...signals };

  // ---- Old key migration ---------------------------------------------------
  // If the legacy "dismissed" key exists for this concept, treat as graduated.

  const hasLegacyDismissed = useMemo(
    () => Boolean(localStorage.getItem(legacyDismissedKey(conceptId))),
    [conceptId]
  );

  // ---- Core step status computation ----------------------------------------

  const coreSteps = useMemo<CoreStepState[]>(() => {
    let foundActive = false;
    return CORE_STEPS.map((step) => {
      const complete = step.isComplete(data);
      const preconditionMet = step.precondition(data);

      if (complete) {
        return { step, status: "complete" as StepStatus };
      }

      if (!foundActive && preconditionMet) {
        foundActive = true;
        return { step, status: "active" as StepStatus };
      }

      return { step, status: "locked" as StepStatus };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(data)]);

  const allCoreComplete = useMemo(
    () => coreSteps.every((s) => s.status === "complete"),
    [coreSteps]
  );

  // ---- Phase computation ---------------------------------------------------

  const phase = useMemo<GuidePhase>(() => {
    // Dev-only override wins over everything else. Never set in production.
    if (userStorage._devForcedPhase) return userStorage._devForcedPhase;
    if (userStorage.expert) return "expert";
    if (userStorage.graduated || hasLegacyDismissed || allCoreComplete)
      return "graduated";
    return "onboarding";
  }, [userStorage, hasLegacyDismissed, allCoreComplete]);

  // ---- Auto-graduation side-effect ----------------------------------------
  // MUST be useEffect, not useMemo — side effects are not allowed in memos.

  useEffect(() => {
    // Skip auto-graduation when a dev override is pinning the phase, so
    // Force Onboarding can stay onboarding on a count-complete concept.
    if (userStorage._devForcedPhase) return;
    if (allCoreComplete && !userStorage.graduated) {
      const updated = { ...userStorage, graduated: true };
      writeUserStorage(updated);
      setUserStorage(updated);
    }
  }, [allCoreComplete, userStorage]);

  // ---- Feature status computation ------------------------------------------

  const features = useMemo<FeatureState[]>(() => {
    const seenIds = new Set(conceptStorage.seenMilestones);
    // Exclude features that are for the dashboard (parentStep === "dashboard")
    return FEATURES.filter((f) => f.parentStep !== "dashboard").map((feature) => {
      const appears = feature.appearThreshold ? feature.appearThreshold(data) : true;

      if (!appears) {
        return { feature, status: "hidden" as FeatureStatus };
      }

      if (feature.isComplete(data)) {
        return { feature, status: "used" as FeatureStatus };
      }

      if (seenIds.has(feature.id)) {
        return { feature, status: "seen" as FeatureStatus };
      }

      if (!feature.precondition(data)) {
        // locked — compute progressText for known features
        let progressText: string | undefined;
        if (feature.id === "field-debate") {
          progressText = `${data.scoredCount} / 15 scored`;
        } else if (feature.id === "working-sets") {
          progressText = `${data.totalPapers} / 30 papers`;
        } else if (feature.id === "coverage-audit") {
          progressText = `${data.scoredCount} / 20 scored`;
        } else if (feature.id === "writing-workspace") {
          progressText = `${data.claimCount} / 3 claims`;
        }
        return { feature, status: "locked" as FeatureStatus, progressText };
      }

      return { feature, status: "unlocked" as FeatureStatus };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(data), conceptStorage.seenMilestones]);

  // ---- Walkthroughs derivation ---------------------------------------------
  // Walkthroughs are always returned as-is — no phase or predicate gating.

  const walkthroughs = useMemo<WalkthroughEntry[]>(() => WALKTHROUGHS, []);

  // ---- justUnlocked tracking -----------------------------------------------
  // Detect transitions into `unlocked` status and queue the feature id in
  // localStorage so that consumers (e.g. JourneyBar auto-expansion) can pick it
  // up even across remounts.

  const prevFeatureStatusRef = useRef<Record<string, FeatureStatus>>({});
  const [justUnlocked, setJustUnlocked] = useState<string[]>(() =>
    readJustUnlocked(conceptId)
  );

  useEffect(() => {
    const current: Record<string, FeatureStatus> = {};
    const newlyUnlocked: string[] = [];
    for (const fs of features) {
      current[fs.feature.id] = fs.status;
      const prev = prevFeatureStatusRef.current[fs.feature.id];
      if (fs.status === "unlocked" && prev && prev !== "unlocked") {
        newlyUnlocked.push(fs.feature.id);
      }
    }
    prevFeatureStatusRef.current = current;

    // Suppress the auto-add when a dev override is pinning the phase. Without
    // this, switching between Force Onboarding (fresh counts) and Force
    // Graduated (real counts) makes every feature look like it just
    // unlocked, flooding the GuidedRunCard with synthetic unlock prompts.
    if (userStorage._devForcedPhase) return;

    if (newlyUnlocked.length > 0) {
      const existing = readJustUnlocked(conceptId);
      const deduped = Array.from(new Set([...existing, ...newlyUnlocked]));
      writeJustUnlocked(conceptId, deduped);
      setJustUnlocked(deduped);
    }
  }, [features, conceptId, userStorage._devForcedPhase]);

  const consumeJustUnlocked = useCallback(
    (id: string) => {
      const remaining = readJustUnlocked(conceptId).filter((x) => x !== id);
      writeJustUnlocked(conceptId, remaining);
      setJustUnlocked(remaining);
    },
    [conceptId]
  );

  // ---- Phase transition tracking ------------------------------------------

  const prevPhaseRef = useRef<GuidePhase | null>(null);
  const [phaseTransition, setPhaseTransition] = useState<{
    from: GuidePhase;
    to: GuidePhase;
  } | null>(null);

  useEffect(() => {
    const prev = prevPhaseRef.current;
    if (prev !== null && prev !== phase) {
      setPhaseTransition({ from: prev, to: phase });
      const t = setTimeout(() => setPhaseTransition(null), 3000);
      prevPhaseRef.current = phase;
      return () => clearTimeout(t);
    }
    prevPhaseRef.current = phase;
  }, [phase]);

  // ---- guide:phase-change event listener -----------------------------------
  // The drawer footer toggle (Chunk 10) dispatches this event when the user
  // flips between onboarding/graduated/expert. Re-read user storage so the
  // hook re-renders with the new phase. Also re-read justUnlocked so the
  // dev panel's Simulate Unlock can seed the queue without a page reload.

  useEffect(() => {
    function handler() {
      setUserStorage(readUserStorage());
      setJustUnlocked(readJustUnlocked(conceptId));
    }
    window.addEventListener("guide:phase-change", handler);
    return () => window.removeEventListener("guide:phase-change", handler);
  }, [conceptId]);

  // ---- Actions -------------------------------------------------------------

  const setEntryPath = useCallback(
    (path: EntryPath) => {
      const updated = { ...conceptStorage, entryPath: path };
      writeConceptStorage(conceptId, updated);
      setConceptStorage(updated);
    },
    [conceptId, conceptStorage]
  );

  const dismissFeature = useCallback(
    (id: string) => {
      // Add to seenMilestones (not permanently dismissed, just acknowledged)
      const alreadySeen = new Set(conceptStorage.seenMilestones);
      if (alreadySeen.has(id)) return;
      alreadySeen.add(id);
      const updated = { ...conceptStorage, seenMilestones: Array.from(alreadySeen) };
      writeConceptStorage(conceptId, updated);
      setConceptStorage(updated);
    },
    [conceptId, conceptStorage]
  );

  const dismissAll = useCallback(() => {
    const updated = { ...userStorage, expert: true };
    writeUserStorage(updated);
    setUserStorage(updated);
  }, [userStorage]);

  // ---- Result --------------------------------------------------------------

  return {
    phase,
    entryPath: conceptStorage.entryPath,
    coreSteps,
    features,
    walkthroughs,
    justUnlocked,
    data,
    phaseTransition,
    setEntryPath,
    dismissFeature,
    dismissAll,
    consumeJustUnlocked,
  };
}
